import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchChannelZipSync,
  requestChannelZipSync,
} from '@src/api/actions/dashboard/facebookZipTargeting';

const POLL_MS = 3000;
const BUSY = ['queued', 'running'];

// Structural skips the sync never touches; shown greyed, not as problems.
const QUIET_BLOCKS = ['single-market campaign', 'ad groups split by geography'];

const MODE_LABELS = {
  apply: 'Automatic sync is ON — every Facebook ZIP push also updates these.',
  report: 'Automatic sync is in REPORT mode — the digest lists what it would push; nothing changes until Push now.',
  off: 'Automatic sync is OFF.',
};

function rowStatus(row) {
  if (row.error) return { label: 'Failed', color: 'error', title: row.error };
  if (row.blocked) {
    const quiet = QUIET_BLOCKS.some((prefix) => row.blocked.startsWith(prefix));
    return { label: quiet ? 'Not synced' : 'Held', color: quiet ? 'default' : 'warning', title: row.blocked };
  }
  if (row.applied) return { label: 'Pushed', color: 'success' };
  if (row.add_count || row.remove_count) return { label: 'Out of sync', color: 'warning' };
  return { label: 'In sync', color: 'success' };
}

/**
 * Google Demand Gen ad groups, Google Search campaigns and Bing campaigns for
 * the selected client, each with its ZIP count against the set Facebook gets.
 * "Check" re-reads the live accounts; staff "Push now" applies the same diff
 * under the reconciler's guards. Both run in the background; this polls.
 */
export default function ChannelZipSyncCard({ clientId, header, isStaff = false, onPushed }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);
  const lastStatus = useRef(null);

  const load = useCallback(() => {
    if (!clientId) return;
    fetchChannelZipSync({ clientId })
      .then((response) => {
        const next = response.data || {};
        setState(next);
        setError('');
        if (BUSY.includes(lastStatus.current) && next.status === 'done' && next.action === 'push') {
          onPushed?.();
        }
        lastStatus.current = next.status;
      })
      .catch((err) => setError(err?.message || 'Failed to load Google & Bing status.'));
  }, [clientId, onPushed]);

  useEffect(() => {
    setState(null);
    lastStatus.current = null;
    load();
  }, [load]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (BUSY.includes(state?.status)) {
      timer.current = setTimeout(load, POLL_MS);
    }
    return () => clearTimeout(timer.current);
  }, [state, load]);

  const start = (action) => {
    setError('');
    requestChannelZipSync({ clientId, action })
      .then((response) => {
        setState(response.data || { status: 'queued', action });
        lastStatus.current = 'queued';
      })
      .catch((err) => setError(err?.message || 'Could not start.'));
  };

  const busy = BUSY.includes(state?.status);
  const rows = state?.rows || [];
  const linked = (state?.google_accounts?.length || 0) + (state?.bing_accounts?.length || 0);
  const outOfSync = rows.filter((row) => !row.blocked && !row.error && !row.applied && (row.add_count || row.remove_count));

  return (
    <Card>
      {header}
      <Divider />
      <CardContent>
        <Stack spacing={1.5}>
          {!clientId ? (
            <Typography color="text.secondary">Pick a client to see its Google and Bing campaigns.</Typography>
          ) : state && !linked ? (
            <Typography color="text.secondary">No Google or Bing account is linked to this client.</Typography>
          ) : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {MODE_LABELS[state?.auto_mode] || ''}{' '}
                  Live campaigns get the same ZIPs as Facebook; paused ones are left alone.
                  {state?.checked_at ? ` Checked ${new Date(state.checked_at).toLocaleString()}.` : ''}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  <Button size="small" variant="outlined" onClick={() => start('check')} disabled={busy}>
                    {busy && state?.action === 'check' ? 'Checking…' : 'Check'}
                  </Button>
                  {isStaff && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => start('push')}
                      disabled={busy || !state?.checked_at || !outOfSync.length}
                      title={!state?.checked_at ? 'Run Check first' : !outOfSync.length ? 'Nothing to push' : ''}
                    >
                      {busy && state?.action === 'push' ? 'Pushing…' : `Push now${outOfSync.length ? ` (${outOfSync.length})` : ''}`}
                    </Button>
                  )}
                </Stack>
              </Stack>
              {busy && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Reading the live Google and Bing accounts — this takes up to a minute.
                  </Typography>
                </Stack>
              )}
              {state?.status === 'error' && <Alert severity="error">{state.error}</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              {rows.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Channel</TableCell>
                        <TableCell>Campaign / ad group</TableCell>
                        <TableCell align="right">Live ZIPs</TableCell>
                        <TableCell align="right">Facebook set</TableCell>
                        <TableCell align="right">Change</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, index) => {
                        const status = rowStatus(row);
                        return (
                          <TableRow key={`${row.channel}-${row.campaign_name}-${row.target}-${index}`}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip label={row.channel} size="small" variant="outlined" sx={{ height: 20 }} />
                            </TableCell>
                            <TableCell sx={{ color: 'text.secondary' }}>
                              {[row.campaign_name, row.target && row.target !== 'campaign' ? row.target : null]
                                .filter(Boolean)
                                .join(' / ') || '—'}
                            </TableCell>
                            <TableCell align="right">{(row.current_count ?? 0).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              {(row.desired_count ?? 0).toLocaleString()}
                              {row.unmapped_count ? (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  title="ZIPs this platform has no location for"
                                >
                                  {' '}({row.unmapped_count} n/a)
                                </Typography>
                              ) : null}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Typography component="span" variant="body2" sx={{ color: 'success.main' }}>
                                +{row.applied ? row.added : row.add_count || 0}
                              </Typography>{' '}
                              <Typography component="span" variant="body2" sx={{ color: 'error.main' }}>
                                −{row.applied ? row.removed : row.remove_count || 0}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={status.label}
                                size="small"
                                color={status.color}
                                title={status.title}
                                sx={{ height: 20 }}
                              />
                              {row.blocked && status.color !== 'default' && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {row.blocked}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                !busy && (
                  <Typography color="text.secondary">
                    {state?.checked_at ? 'No live Google or Bing campaigns.' : 'Press Check to read the live Google and Bing campaigns.'}
                  </Typography>
                )
              )}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
